import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import Button from '../../../../shared/_TBComponents/Button';
import Icon from '../../../../shared/_TBComponents/Icon';
import Input from '../../../../shared/_TBComponents/Input';
import Spinner from '../../../../shared/_TBComponents/Spinner';

const SelectorSection = styled.div`
  width: ${(props) => (props.fullWidth ? '100%' : `${props.width}px`)};
  flex-shrink: ${(props) => (props.fullWidth ? 1 : 0)};
  flex-grow: ${(props) => (props.fullWidth ? 1 : 0)};
  min-height: 0;
  border-right: ${(props) => (props.fullWidth ? 'none' : '1px solid #e0e0e0')};
  display: flex;
  flex-direction: column;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
  padding: 0px 16px;
  margin-top: 16px;
`;

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0px 16px;
  margin: 16px 0;
`;

const CustomInputWrapper = styled.div`
  width: 100%;
  input {
    border: 2px solid #E0E0E0 !important;
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 30px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  display: flex;
  align-items: center;
  z-index: 2;
`;

const ScrollList = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 0px 16px;
  margin-bottom: 8px;
  position: relative;

  &::-webkit-scrollbar {
    width: 8px;
    opacity: 0;
    transition: opacity 0.3s;
  }
  &:hover::-webkit-scrollbar {
    opacity: 1;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #999;
  }

  scrollbar-width: thin;
  scrollbar-color: #ccc #f1f1f1;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
`;

const NavigatorSelectorPanel = ({
  width,
  fullWidth,
  buttons,
  searchPlaceholder,
  isLoading,
  selectedId,
  restoreSelectedScrollRef,
  renderList,
}) => {
  const [searchFilter, setSearchFilter] = useState('');

  const listContainerRef = useRef(null);
  const selectedItemRef = useRef(null);
  const selectedScrollRef = useRef(false);
  const initialMountRef = useRef(restoreSelectedScrollRef);

  const scrollToTop = () => {
    if (!listContainerRef.current) return;
    listContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Restore selected item position when returning
  useEffect(() => {
    if (!initialMountRef.current || selectedScrollRef.current) return;
    if (selectedId && selectedItemRef.current && listContainerRef.current) {
      setTimeout(() => {
        const element = selectedItemRef.current;
        const container = listContainerRef.current;
        if (!element || !container) return;

        const containerRect = container.getBoundingClientRect();
        const itemRect = element.getBoundingClientRect();

        // For sticky headers just account for height on autoscroll
        let headerHeight = 0;
        try {
          const stickyElems = container.querySelectorAll('[data-sticky-header]');
          if (stickyElems && stickyElems.length > 0) {
            headerHeight = Array.from(stickyElems).reduce((sum, el) => sum
             + (el.offsetHeight || 0), 0);
          }
        } catch (e) {
          headerHeight = 0;
        }

        const buffer = 80;
        const extraBuffer = headerHeight > 0 ? 10 : 0;
        const scrollOffset = itemRect.top - containerRect.top
        + container.scrollTop - headerHeight + extraBuffer - buffer;
        container.scrollTo({ top: scrollOffset, behavior: 'smooth' });
        selectedScrollRef.current = true;
      }, 100);
    }
  }, [selectedId, isLoading]);

  return (
    <SelectorSection width={width} fullWidth={fullWidth}>
      {buttons && buttons.length > 0 && (
        <ButtonContainer>
          {buttons.map((btn) => (
            <Button
              key={btn.name}
              name={btn.name}
              onClick={btn.onClick}
              href={btn.href}
              bgColor='white'
              borderColor='#ddd'
              txtColor='#333'
              fullWidth
              customBorderRadius='4px'
              styledWrapperProps={{
                height: '40px',
                padding: '6px 16px',
                boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)',
              }}
              styledTextProps={{
                fontSize: '16px',
                fontWeight: 700,
                letterSpacing: '0.3px',
              }}
              {...(btn.icon ? { icon: btn.icon } : {})}
              {...(btn.iconSize ? { iconSize: btn.iconSize } : {})}
              {...(btn.iconColor ? { iconColor: btn.iconColor } : {})}
            />
          ))}
        </ButtonContainer>
      )}
      <SearchContainer>
        <SearchIconWrapper>
          <Icon
            icon='magnifying-glass'
            size={16}
            color='#9E9E9E'
            styledProps={{ mb: '4px' }}
          />
        </SearchIconWrapper>
        <CustomInputWrapper>
          <Input
            inputOnly
            placeholder={searchPlaceholder}
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            styledPropsInput={{
              width: '100%',
              height: '45px',
              fontSize: '19px',
              fontWeight: 400,
              pl: '44px',
              pr: '16px',
              py: '8px',
              boxSizing: 'border-box',
            }}
          />
        </CustomInputWrapper>
      </SearchContainer>
      {isLoading ? (
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      ) : (
        <ScrollList ref={listContainerRef}>
          {renderList({ selectedRef: selectedItemRef, searchFilter, scrollToTop })}
        </ScrollList>
      )}
    </SelectorSection>
  );
};

NavigatorSelectorPanel.propTypes = {
  width: PropTypes.number.isRequired,
  fullWidth: PropTypes.bool,
  buttons: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  })),
  searchPlaceholder: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  restoreSelectedScrollRef: PropTypes.bool,
  renderList: PropTypes.func.isRequired,
};

NavigatorSelectorPanel.defaultProps = {
  fullWidth: false,
  buttons: [],
  selectedId: null,
  restoreSelectedScrollRef: true,
};

export default NavigatorSelectorPanel;
